import { useState, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  Zap, Heart, Flame, CheckCircle, RefreshCw, X, Lightbulb,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Backward-compat: quiz sorularini flat ya da nested alandan al.
 * @param {Object} lesson
 * @returns {Array}
 */
function getQuizQuestions(lesson) {
  return lesson.quiz_questions || lesson.quiz?.questions || lesson.questions || [];
}

/**
 * QuizLesson — backend destekli Duolingo-tarzi quiz akisi (lesson_type==='quiz').
 * VideoLesson'daki quiz panelinin bagimsiz bileşen hali.
 *
 * @param {{ lesson: Object, courseId: string, onComplete: Function, completedLessonIds: string[] }} props
 */
export default function QuizLesson({ lesson, courseId, onComplete, completedLessonIds = [] }) {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  const lessonId = lesson.lesson_id;
  const isCompleted = completedLessonIds.includes(lessonId);
  const initialQuestions = getQuizQuestions(lesson);

  const [questions] = useState(initialQuestions);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [fillAnswer, setFillAnswer] = useState('');
  const [answerResult, setAnswerResult] = useState(null);
  const [totalXpEarned, setTotalXpEarned] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [lives, setLives] = useState(5);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [shakeAnim, setShakeAnim] = useState(false);
  const [completing, setCompleting] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];

  const startQuizSession = useCallback(async () => {
    try {
      const res = await axios.post(
        `${API}/quiz/session/start`,
        { lesson_id: lessonId, course_id: courseId },
        { headers: authHeaders() }
      );
      setSessionId(res.data.session_id);
    } catch (err) {
      console.error('Session start error:', err);
    }
  }, [lessonId, courseId, authHeaders]);

  // Start on mount if questions exist
  useState(() => {
    if (questions.length) startQuizSession();
  });

  const checkAnswer = async () => {
    if (!currentQuestion) return;
    const answer = currentQuestion.type === 'fill_blank' ? fillAnswer : selectedAnswer;
    if (!answer) return;
    try {
      const res = await axios.post(`${API}/quiz/answer`, {
        lesson_id: lessonId,
        question_index: currentQuestionIndex,
        answer,
        session_id: sessionId,
      }, { headers: authHeaders() });
      setAnswerResult(res.data);
      if (res.data.correct) {
        setCorrectCount(prev => prev + 1);
        setTotalXpEarned(prev => prev + (res.data.xp_earned || 0));
        setStreak(res.data.current_streak || 0);
        setBestStreak(prev => Math.max(prev, res.data.current_streak || 0));
      } else {
        setLives(res.data.lives_remaining);
        setStreak(0);
        setShakeAnim(true);
        setTimeout(() => setShakeAnim(false), 600);
        if (res.data.game_over) setGameOver(true);
      }
    } catch (err) {
      console.error('Answer error:', err);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setFillAnswer('');
      setAnswerResult(null);
      setShowHint(false);
    } else {
      setQuizCompleted(true);
      if (sessionId) {
        axios.post(`${API}/quiz/session/complete`, { session_id: sessionId }, { headers: authHeaders() }).catch(() => {});
      }
    }
  };

  const retryQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setFillAnswer('');
    setAnswerResult(null);
    setQuizCompleted(false);
    setCorrectCount(0);
    setTotalXpEarned(0);
    setGameOver(false);
    setLives(5);
    setStreak(0);
    setShowHint(false);
    startQuizSession();
  };

  const handleComplete = async () => {
    if (completing) return;
    const score = questions.length ? Math.floor((correctCount / questions.length) * 100) : 0;
    setCompleting(true);
    try {
      await axios.post(
        `${API}/enrollments/complete-lesson`,
        { course_id: courseId, lesson_id: lessonId, quiz_score: score },
        { headers: authHeaders() }
      );
      toast.success(lang === 'tr' ? 'Ders tamamlandı!' : 'Lesson completed!');
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCompleting(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="card-clay p-4 mb-6 text-center bg-green-50 border-green-200" data-testid="lesson-already-completed">
        <CheckCircle size={20} className="inline text-green-600 mr-2" />
        <span className="font-bold text-sm text-green-700">
          {lang === 'tr' ? 'Bu ders tamamlandı' : 'This lesson is completed'}
        </span>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="card-clay p-8 text-center text-[var(--text-muted)]" data-testid="quiz-no-questions">
        <p className="font-semibold">
          {lang === 'tr' ? 'Bu ders için soru bulunamadı.' : 'No questions found for this lesson.'}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Status bar */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-1" data-testid="lives-display">
          {[...Array(5)].map((_, i) => (
            <Heart
              key={i}
              size={18}
              className={`transition-all duration-300 ${i < lives ? 'text-red-500 fill-red-500' : 'text-gray-300'}`}
            />
          ))}
        </div>
        {streak > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="flex items-center gap-1 bg-orange-100 px-2 py-1 rounded-lg"
            data-testid="streak-display"
          >
            <Flame size={14} className="text-orange-500" />
            <span className="text-xs font-bold text-orange-600">{streak}</span>
          </motion.div>
        )}
        <div className="ml-auto flex items-center gap-1 text-sm font-bold" data-testid="xp-display">
          <Zap size={16} strokeWidth={2.5} className="text-[var(--brand-secondary)]" />
          {totalXpEarned} XP
        </div>
      </div>

      {/* Game Over */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card-clay p-8 text-center mb-6"
          data-testid="game-over"
        >
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <Heart size={40} className="text-red-400" />
          </div>
          <h2 className="font-display text-3xl font-bold mb-2 text-red-500">
            {lang === 'tr' ? 'Canlar Bitti!' : 'No Lives Left!'}
          </h2>
          <p className="text-[var(--text-muted)] font-semibold mb-6">
            {correctCount}/{questions.length} {lang === 'tr' ? 'doğru cevap' : 'correct answers'}
          </p>
          <button onClick={retryQuiz} className="btn-primary px-8 py-3 flex items-center gap-2 mx-auto" data-testid="retry-quiz-btn">
            <RefreshCw size={18} /> {lang === 'tr' ? 'Tekrar Dene' : 'Try Again'}
          </button>
        </motion.div>
      )}

      {/* Quiz Completed */}
      {quizCompleted && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="card-clay p-8 text-center mb-6"
          data-testid="quiz-complete"
        >
          <div className="w-20 h-20 rounded-full bg-[var(--brand-secondary)] flex items-center justify-center mx-auto mb-6">
            <Zap size={40} strokeWidth={2.5} className="text-[var(--text-main)]" />
          </div>
          <h2 className="font-display text-3xl font-bold mb-2">
            {correctCount === questions.length
              ? (lang === 'tr' ? 'Mükemmel!' : 'Perfect!')
              : correctCount >= questions.length * 0.7
              ? (lang === 'tr' ? 'Harika!' : 'Great!')
              : (lang === 'tr' ? 'İyi deneme!' : 'Good try!')}
          </h2>
          <p className="text-[var(--text-muted)] font-semibold mb-6">
            {correctCount}/{questions.length} {lang === 'tr' ? 'doğru cevap' : 'correct answers'}
          </p>
          <div className="grid grid-cols-3 gap-4 mb-8 max-w-sm mx-auto">
            <div className="bg-[#FFF4CC] p-3 rounded-xl">
              <Zap size={20} className="mx-auto text-[var(--brand-secondary-shadow)] mb-1" />
              <p className="font-bold text-lg">{totalXpEarned}</p>
              <p className="text-[10px] font-bold text-[var(--text-muted)]">XP</p>
            </div>
            <div className="bg-orange-50 p-3 rounded-xl">
              <Flame size={20} className="mx-auto text-orange-500 mb-1" />
              <p className="font-bold text-lg">{bestStreak}</p>
              <p className="text-[10px] font-bold text-[var(--text-muted)]">{lang === 'tr' ? 'En Iyi Seri' : 'Best Streak'}</p>
            </div>
            <div className="bg-red-50 p-3 rounded-xl">
              <Heart size={20} className="mx-auto text-red-500 fill-red-500 mb-1" />
              <p className="font-bold text-lg">{lives}/5</p>
              <p className="text-[10px] font-bold text-[var(--text-muted)]">{lang === 'tr' ? 'Kalan Can' : 'Lives Left'}</p>
            </div>
          </div>
          <div className="flex justify-center gap-4">
            <button
              onClick={handleComplete}
              disabled={completing}
              className="btn-success px-8 py-3 flex items-center gap-2 disabled:opacity-50"
              data-testid="complete-lesson-btn"
            >
              <CheckCircle size={18} />
              {completing ? '...' : (lang === 'tr' ? 'Dersi Tamamla' : 'Complete Lesson')}
            </button>
            <button onClick={retryQuiz} className="btn-tactile px-6 py-3 bg-white border-[var(--border-light)] flex items-center gap-2" data-testid="retry-quiz-from-complete-btn">
              <RefreshCw size={16} /> {lang === 'tr' ? 'Tekrar' : 'Retry'}
            </button>
          </div>
        </motion.div>
      )}

      {/* Active Question */}
      {!gameOver && !quizCompleted && currentQuestion && (
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className={`card-clay p-8 mb-6 ${shakeAnim ? 'animate-shake' : ''}`}
            data-testid="quiz-container"
          >
            <div className="flex items-center justify-between mb-6">
              <span className="text-sm font-bold text-[var(--text-muted)]">
                {lang === 'tr' ? 'Soru' : 'Question'} {currentQuestionIndex + 1}/{questions.length}
              </span>
            </div>

            <div className="flex items-center gap-1 mb-4">
              <Zap size={14} className="text-[var(--brand-secondary)]" />
              <span className="text-xs font-bold text-[var(--brand-secondary-shadow)]">
                +{currentQuestion.xp_value || 10} XP
              </span>
            </div>

            <h3 className="font-display text-xl font-bold mb-8">{currentQuestion.question}</h3>

            {/* Multiple Choice / True-False */}
            {currentQuestion.type !== 'fill_blank' && (
              <div className="space-y-3 mb-8">
                {(currentQuestion.options || []).map((option, i) => {
                  let classes = 'quiz-option';
                  if (answerResult) {
                    if (option.toLowerCase() === answerResult.correct_answer?.toLowerCase()) classes += ' correct';
                    else if (option === selectedAnswer && !answerResult.correct) classes += ' incorrect';
                  } else if (option === selectedAnswer) {
                    classes += ' selected';
                  }
                  return (
                    <motion.button
                      key={i}
                      whileTap={!answerResult ? { scale: 0.98 } : {}}
                      onClick={() => !answerResult && setSelectedAnswer(option)}
                      className={`w-full text-left ${classes}`}
                      disabled={!!answerResult}
                      data-testid={`quiz-option-${i}`}
                    >
                      <span className="mr-3 inline-flex items-center justify-center w-8 h-8 rounded-lg border-2 border-current text-sm font-bold">
                        {String.fromCharCode(65 + i)}
                      </span>
                      {option}
                    </motion.button>
                  );
                })}
              </div>
            )}

            {/* Fill in the Blank */}
            {currentQuestion.type === 'fill_blank' && (
              <div className="mb-8">
                <input
                  type="text"
                  value={fillAnswer}
                  onChange={(e) => setFillAnswer(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !answerResult && fillAnswer && checkAnswer()}
                  disabled={!!answerResult}
                  placeholder={lang === 'tr' ? 'Cevabinizi yazin...' : 'Type your answer...'}
                  className={`w-full px-5 py-4 rounded-xl border-3 text-lg font-bold text-center focus:outline-none transition-all ${
                    answerResult
                      ? answerResult.correct
                        ? 'border-[var(--brand-success)] bg-green-50'
                        : 'border-[var(--brand-danger)] bg-red-50'
                      : 'border-[var(--border-light)] bg-gray-50 focus:border-[var(--brand-primary)]'
                  }`}
                  data-testid="fill-blank-input"
                />
                {answerResult && !answerResult.correct && (
                  <p className="text-center mt-2 text-sm font-bold text-[var(--brand-success)]">
                    {lang === 'tr' ? 'Doğru cevap: ' : 'Correct answer: '}{answerResult.correct_answer}
                  </p>
                )}
              </div>
            )}

            {/* Hint */}
            {currentQuestion.hint && !answerResult && (
              <div className="mb-4">
                {!showHint ? (
                  <button
                    onClick={() => setShowHint(true)}
                    className="text-sm font-semibold text-[var(--brand-primary)] flex items-center gap-1 hover:underline"
                    data-testid="show-hint-btn"
                  >
                    <Lightbulb size={14} /> {lang === 'tr' ? 'İpucu Göster' : 'Show hint'}
                  </button>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bg-blue-50 border-2 border-blue-200 rounded-xl p-3 flex items-start gap-2"
                  >
                    <Lightbulb size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-semibold text-blue-700">{currentQuestion.hint}</p>
                  </motion.div>
                )}
              </div>
            )}

            {/* Answer Result */}
            {answerResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl mb-6 ${answerResult.correct ? 'bg-green-50 border-2 border-[var(--brand-success)]' : 'bg-red-50 border-2 border-[var(--brand-danger)]'}`}
                data-testid="answer-result"
              >
                <div className="flex items-center gap-2 mb-1">
                  {answerResult.correct
                    ? <CheckCircle size={20} className="text-[var(--brand-success)]" />
                    : <X size={20} className="text-[var(--brand-danger)]" />}
                  <span className="font-bold text-lg">
                    {answerResult.correct ? (lang === 'tr' ? 'Doğru!' : 'Correct!') : (lang === 'tr' ? 'Yanlış!' : 'Incorrect!')}
                  </span>
                </div>
                <p className="text-sm font-medium ml-7">{answerResult.explanation}</p>
              </motion.div>
            )}

            {/* Actions */}
            <div className="flex justify-end">
              {!answerResult ? (
                <button
                  onClick={checkAnswer}
                  disabled={currentQuestion.type === 'fill_blank' ? !fillAnswer : !selectedAnswer}
                  className="btn-primary px-8 py-3 disabled:opacity-50"
                  data-testid="check-answer-btn"
                >
                  {lang === 'tr' ? 'Kontrol Et' : 'Check'}
                </button>
              ) : (
                <button
                  onClick={nextQuestion}
                  className="btn-success px-8 py-3"
                  data-testid="next-question-btn"
                >
                  {currentQuestionIndex < questions.length - 1
                    ? (lang === 'tr' ? 'Devam' : 'Continue')
                    : (lang === 'tr' ? 'Bitir' : 'Finish')}
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </>
  );
}
